import { useMemo } from 'react';
import { resolveEntitlements, can as canFn, quota as quotaFn } from '../utils/entitlements';
import { useAuth, type EnterpriseAccess } from '../context/AuthContext';
import type { UserSession } from '../types';

// Pure derivation (also used directly in tests). Fail-closed (#13): a missing (null) or
// unknown plan resolves to 'freemium', the most restrictive tier — never 'enterprise'.
// "Not loaded yet" is NOT a missing plan: planForUi() handles it upstream (pending).
export function deriveEntitlements(plan: unknown, overrides: unknown) {
  const ent = resolveEntitlements(plan, overrides);
  return {
    ent,
    can: (feature: string) => canFn(ent, feature),
    quota: (metric: string) => quotaFn(ent, metric),
  };
}

export type AccessFields = Pick<UserSession, 'plan' | 'entitlement_overrides' | 'access_mode' | 'mask_candidate_contacts' | 'subscription'>;

/**
 * Forfait / accès EFFECTIFS : ceux de l'entreprise sélectionnée quand ils sont connus, sinon ceux
 * de `user` (1re adhésion). Pur. Un superadmin garde les siens : ses droits ne dépendent d'aucune
 * entreprise, et ce correctif ne doit rien changer pour lui.
 */
export function effectiveAccess(user: UserSession | null | undefined, active: EnterpriseAccess | null | undefined): AccessFields {
  const useActive = !!active && user?.role !== 'superadmin';
  const src = useActive ? active : user;
  return {
    // user.plan : undefined CONSERVÉ = « pas encore lu » (voir planForUi) ; null = lu, absent.
    // Une entreprise sélectionnée est par définition LUE → jamais undefined (null si sans forfait).
    plan: useActive ? (active?.plan ?? null) : user?.plan,
    entitlement_overrides: src?.entitlement_overrides ?? null,
    access_mode: src?.access_mode ?? null,
    mask_candidate_contacts: src?.mask_candidate_contacts ?? null,
    subscription: src?.subscription ?? null,
  };
}

/**
 * Forfait à appliquer dans l'INTERFACE, avec son état de lecture. Pur. (#13)
 * - super-admin → 'enterprise' explicitement : ses droits ne dépendent d'aucune entreprise
 *   (le serveur le court-circuite déjà dans enforce()).
 * - `pending` = PAS ENCORE LU, et SEULEMENT pendant le chargement : session en restauration
 *   (`loading`) ou utilisateur connecté dont la RPC get_user_enterprise n'a pas encore répondu
 *   (`user.plan === undefined`). Aucun blocage tant que la lecture n'est pas finie (le plan
 *   rendu est alors 'enterprise'). Une fois la lecture finie — même en ÉCHEC — AuthContext pose
 *   le forfait du cache ou null : undefined ne survit jamais au chargement.
 * - `plan === null` = LU et ABSENT → freemium (fail-closed), comme toute valeur inconnue.
 * - déconnecté et chargement fini → pas pending → freemium (rien à lire, rien d'accordé).
 * Ne pas confondre « absent » et « pas encore lu » : seul le premier ferme l'accès.
 */
export function planForUi(
  user: UserSession | null | undefined,
  active: EnterpriseAccess | null | undefined,
  loading: boolean | undefined,
): { plan: unknown; pending: boolean } {
  if (user?.role === 'superadmin') return { plan: 'enterprise', pending: false };
  const plan = effectiveAccess(user, active).plan;
  const pending = !!loading || (!!user?.loggedIn && plan === undefined);
  return { plan: pending ? 'enterprise' : (plan ?? null), pending };
}

// UX gating only — the real barrier is server-side in edge functions.
export function useEntitlements() {
  const { user, activeAccess, loading } = useAuth();
  const acc = effectiveAccess(user, activeAccess);
  const { plan, pending } = planForUi(user, activeAccess, loading);
  return useMemo(() => {
    const d = deriveEntitlements(plan, acc.entitlement_overrides ?? null);
    const restricted = acc.access_mode === 'restricted';
    const banner: null | 'past_due' | 'restricted' = restricted
      ? 'restricted'
      : (acc.subscription?.status === 'past_due' ? 'past_due' : null);
    return { ...d, pending, restricted, maskCandidateContacts: !!acc.mask_candidate_contacts, banner };
  }, [plan, pending, acc.entitlement_overrides, acc.access_mode, acc.mask_candidate_contacts, acc.subscription]);
}
